/**
 * UPT-2079 L3 (§5.5, §5.3.1) — valeurs par défaut du Concepteur : `emptyModule(dossier)` (module vide v1.1) et les
 * modèles de `entity.add`, `field.add`, `relation.add`, `page.add`. Fonctions pures ; chaque valeur respecte les motifs
 * et minima du schéma (`doc` ≥ 10, `description` ≥ 20, `summary` 10..300, `version` semver).
 */
import type { DesignDossier, DesignModuleSpecJson } from '../data/contracts';
import type { ModuleSpecEntity, ModuleSpecField, ModuleSpecPage } from './specTypes';

export const MODULE_CODE_PATTERN = /^[a-z][a-z0-9-]{1,40}$/;
export const SLUG_PATTERN = /^[a-z][a-z0-9/-]*$/;
export const SEMVER_PATTERN = /^[0-9]+\.[0-9]+\.[0-9]+$/;
export const SCHEMA_PATTERN = /^module_[a-z0-9_]{2,40}$/;
export const DOMAIN_PATTERN = /^[a-z][a-z0-9-]{1,30}$/;
export const PERMISSION_ENTITY_PATTERN = /^[A-Z][A-Z0-9_]{1,60}$/;

export const SUMMARY_MIN = 10;
export const SUMMARY_MAX = 300;
export const DOC_MIN = 10;
export const DESCRIPTION_MIN = 20;
export const DESCRIPTION_MAX = 3000;
export const DEFAULT_SUMMARY = 'Module conçu dans Module Studio.';
export const DEFAULT_ENTITY_DOC = 'À documenter : rôle de cette entité.';
export const DEFAULT_PAGE_DESCRIPTION = 'Page sur mesure à décrire dans ce dossier.';

/** `dz-` retiré, puis le motif du module (minuscules, tirets ; tronqué à 41). */
export const toModuleCode = (dossierCode: string): string => {
  const base = dossierCode.toLowerCase().replace(/^dz-/, '').replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  const trimmed = base.slice(0, 41);
  return MODULE_CODE_PATTERN.test(trimmed) ? trimmed : 'module';
};

/** `module_<code sans tirets>` (motif `^module_[a-z0-9_]{2,40}$`). */
export const schemaForCode = (moduleCode: string): string => {
  const body = moduleCode.replace(/-/g, '').slice(0, 40);
  return `module_${body.length >= 2 ? body : `${body}x`.slice(0, 2)}`;
};

/** `domain` = code sans tirets (motif `^[a-z][a-z0-9-]{1,30}$`). */
export const domainForCode = (moduleCode: string): string => {
  const body = moduleCode.replace(/-/g, '').slice(0, 31);
  return body.length >= 2 ? body : `${body}x`.slice(0, 2);
};

export const emptyModule = (dossier: Pick<DesignDossier, 'code' | 'name' | 'summary' | 'moduleCode'>): DesignModuleSpecJson => {
  const code = dossier.moduleCode && MODULE_CODE_PATTERN.test(dossier.moduleCode) ? dossier.moduleCode : toModuleCode(dossier.code);
  const summary = typeof dossier.summary === 'string' && dossier.summary.trim().length >= SUMMARY_MIN
    ? dossier.summary.trim().slice(0, SUMMARY_MAX)
    : DEFAULT_SUMMARY;
  const name = (dossier.name ?? '').trim();
  return {
    specVersion: '1.1',
    module: {
      code,
      name: name.length >= 2 ? name.slice(0, 80) : code,
      domain: domainForCode(code),
      schema: schemaForCode(code),
      category: 'other',
      version: '0.1.0',
      summary,
      locales: ['fr'],
      portals: ['portal'],
    },
    entities: {},
  };
};

/** Modèle de `entity.add` (§5.3.1). */
export const newEntity = (index: number, schema: string, code: string): ModuleSpecEntity => ({
  label: { singular: { fr: `Entité ${index}` }, plural: { fr: `Entités ${index}` } },
  doc: DEFAULT_ENTITY_DOC,
  kind: 'master',
  table: `${schema}.${code}`,
  fields: [{ name: 'name', type: 'text', label: { fr: 'Nom' }, required: true }],
  screens: {
    list: { columns: ['name'] },
    form: { tabs: [{ key: 'general', label: { fr: 'Général' }, fields: ['name'] }] },
  },
});

/** Modèle de `field.add` (§5.3.1). */
export const newField = (index: number): ModuleSpecField => ({
  name: `champ${index}`,
  type: 'text',
  label: { fr: `Champ ${index}` },
});

/** Modèle de `page.add` (§5.3.1). */
export const newPage = (index: number): ModuleSpecPage => ({
  key: `page${index}`,
  kind: 'custom',
  layout: 'full',
  label: { fr: `Page ${index}` },
  description: DEFAULT_PAGE_DESCRIPTION,
  portals: ['portal'],
});

/** Premier numéro libre pour un préfixe (`entite1`, `champ2`…). */
export const firstFree = (prefix: string, taken: readonly string[]): { index: number; code: string } => {
  let index = 1;
  while (taken.includes(`${prefix}${index}`)) index += 1;
  return { index, code: `${prefix}${index}` };
};
